import React from 'react';
import { Button } from '@/components/ui/button';
import { FileSpreadsheet, Download } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { cn } from '@/lib/utils';
import type { ExportButtonVariant, ExportIconType } from './types';

export interface ExportButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ExportButtonVariant;
  iconType?: ExportIconType;
  label?: string;
  isLoading?: boolean;
  loadingLabel?: string;
  className?: string;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  variant = 'purple',
  iconType,
  label = 'Export',
  isLoading = false,
  loadingLabel = 'Exporting...',
  className,
  disabled,
  ...props
}) => {
  const resolvedIconType: ExportIconType =
    iconType || (variant === 'purple' ? 'spreadsheet' : 'download');

  const renderIcon = () => {
    if (resolvedIconType === 'spreadsheet') {
      return (
        <FileSpreadsheet
          className={cn('w-4 h-4', variant === 'purple' ? 'text-[#A655F8]' : 'text-slate-600')}
        />
      );
    }
    return <Download className="w-4 h-4 mr-2" />;
  };

  if (variant === 'purple') {
    return (
      <Button
        variant="outline"
        disabled={disabled || isLoading}
        className={cn(
          'border-[#E9D5FF] hover:border-[#D8B4FE] text-[#A655F8] bg-white hover:bg-[#FAF5FF]/50 rounded-xl px-4 py-2 flex items-center gap-2 font-semibold shadow-sm transition-all',
          className
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <LoadingSpinner size="small" className="text-[#A655F8]" />
            <span>{loadingLabel}</span>
          </>
        ) : (
          <>
            {renderIcon()}
            <span>{label}</span>
          </>
        )}
      </Button>
    );
  }

  // Standard outline or other variants matching existing toolbar buttons
  return (
    <Button
      variant="outline"
      disabled={disabled || isLoading}
      className={className}
      {...props}
    >
      {isLoading ? (
        <LoadingSpinner size="small" label={loadingLabel} />
      ) : (
        <>
          {renderIcon()}
          <span>{label}</span>
        </>
      )}
    </Button>
  );
};
